import useCreateCallbackForEvent from '@web-common/hooks/useCreateCallbackForEvent';
import useDebounceForEvent from '@web-common/hooks/useDebounceForEvent';
import useThrottleForEvent from '@web-mail/hooks/useThrottleForEvent';
import useStateRef from '@web-mail/hooks/useStateRef';
import React, { useEffect, useCallback, useMemo, useState, useContext, useRef } from 'react';
import LevelQueue, { TAB_SIZE } from './levelQueue';
const tabSizeValues = Object.values(TAB_SIZE).filter(item => typeof item === 'number');
const getLocalKey = () => {
    return new Date().getTime().toString() + Math.random().toString().slice(2);
};
const TabContext = React.createContext({
    onOperTabItemResize: () => { },
    getLocalKey,
    onItemInit: () => { },
    onItemUnmount: () => { },
});
const AutoSizeOperTab = props => {
    const wrapRef = useRef(null);
    const key2sizeMapRef = useRef({});
    const key2levelMapRef = useRef({});
    const key2refMapRef = useRef({});
    const size2widthMapRef = useRef({});
    const initOrderRef = useRef([]);
    const [levelQueue] = useState(new LevelQueue(tabSizeValues));
    const getWidthBySize = useCreateCallbackForEvent((key) => {
        const curSize = key2sizeMapRef.current[key] || TAB_SIZE.NORMAIL;
        return size2widthMapRef.current[key][curSize];
    });
    const getWidthMapBySize = useCreateCallbackForEvent((key) => {
        return size2widthMapRef.current[key];
    });
    const buildSizeState = useCreateCallbackForEvent(() => {
        var _a;
        const wrapWidth = ((_a = wrapRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
        const argList = initOrderRef.current.map((key, index) => {
            const level = key2levelMapRef.current[key] || 0;
            const width = getWidthBySize(key);
            return {
                size: TAB_SIZE.NORMAIL,
                level,
                width,
                index,
                key,
                size2widthMap: getWidthMapBySize(key),
            };
        });
        levelQueue.update(argList);
        levelQueue.getChabuduodeState(wrapWidth);
        const resList = levelQueue.getFinalState();
        resList.forEach(item => {
            const operateItem = key2refMapRef.current[item.key];
            if (operateItem === null || operateItem === void 0 ? void 0 : operateItem.setTapSize) {
                key2levelMapRef.current[item.key] = item.level;
                if (key2sizeMapRef.current[item.key] != item.size) {
                    operateItem === null || operateItem === void 0 ? void 0 : operateItem.setTapSize(item.size);
                    key2sizeMapRef.current[item.key] = item.size;
                }
            }
        });
    });
    const buildSizeStateDebounce = useDebounceForEvent(buildSizeState, 300, { leading: false, trailing: true });
    const buildSizeStateThrottle = useThrottleForEvent(buildSizeState, 200, { leading: false, trailing: true });
    const onOperTabItemResize = useCreateCallbackForEvent((key, config) => {
        if (key && size2widthMapRef.current[key]) {
            const { size, width } = config;
            if (size2widthMapRef.current[key][size] != width) {
                size2widthMapRef.current[key][size] = width;
                buildSizeStateDebounce();
            }
        }
    });
    const onItemInit = useCallback((key, { level, sizeWidthMap }, refConfig) => {
        if (!key2refMapRef.current[key]) {
            initOrderRef.current.push(key);
            key2refMapRef.current[key] = refConfig;
        }
        size2widthMapRef.current[key] = sizeWidthMap;
        key2levelMapRef.current[key] = level;
        buildSizeStateDebounce();
    }, []);
    const onItemUnmount = useCallback((key) => {
        initOrderRef.current = initOrderRef.current.filter(item => item !== key);
        key2refMapRef.current[key] = null;
        key2sizeMapRef.current[key] = null;
        size2widthMapRef.current[key];
        key2levelMapRef.current[key] = null;
        buildSizeStateDebounce();
    }, []);
    useEffect(() => {
        const wrap = wrapRef === null || wrapRef === void 0 ? void 0 : wrapRef.current;
        if (!wrap)
            return;
        const resizeObserver = new ResizeObserver(entries => {
            buildSizeStateThrottle();
        });
        resizeObserver.observe(wrap);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    const domProps = useMemo(() => {
        return Object.assign(Object.assign({}, props), { children: undefined });
    }, [props]);
    return (React.createElement(TabContext.Provider, { value: {
            onOperTabItemResize,
            getLocalKey,
            onItemInit,
            onItemUnmount,
        } },
        React.createElement("div", Object.assign({}, domProps, { ref: wrapRef }), props === null || props === void 0 ? void 0 : props.children)));
};
const AutoSizeOperTabItem = (props) => {
    const TabProps = useContext(TabContext);
    const { onOperTabItemResize, getLocalKey, onItemInit, onItemUnmount } = TabProps;
    const { level = 0, tabkey, gapSize = 0, defaultWidth, smallWidth, miniWidth } = props;
    const [size, setSize] = useState(TAB_SIZE.NORMAIL);
    const itemWrapRef = useRef(null);
    const onOperTabItemResizeRef = useCreateCallbackForEvent(onOperTabItemResize);
    const [localKey] = useState(!tabkey ? getLocalKey() : tabkey);
    const keyRef = useStateRef(localKey);
    const sizeRef = useStateRef(size);
    const levelRef = useStateRef(level);
    const setTapSize = useCallback((size) => {
        setSize(size);
    }, []);
    const localSizeWidthMap = useMemo(() => {
        return {
            [TAB_SIZE.MINI]: miniWidth || smallWidth || defaultWidth,
            [TAB_SIZE.SMALL]: smallWidth || defaultWidth,
            [TAB_SIZE.NORMAIL]: defaultWidth,
        };
    }, [defaultWidth, smallWidth, miniWidth]);
    const localSizeWidthMapRef = useStateRef(localSizeWidthMap);
    const getKey = useCallback(() => {
        return keyRef.current;
    }, []);
    useEffect(() => {
        var _a;
        const resMap = {};
        let curWidth = (((_a = itemWrapRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0) + gapSize;
        for (let i = tabSizeValues.length - 1; i >= 0; i--) {
            const key = tabSizeValues[i];
            if ((localSizeWidthMapRef === null || localSizeWidthMapRef === void 0 ? void 0 : localSizeWidthMapRef.current[key]) == null) {
                resMap[key] = curWidth;
            }
            else {
                resMap[key] = localSizeWidthMapRef.current[key];
                curWidth = localSizeWidthMapRef.current[key];
            }
        }
        onItemInit === null || onItemInit === void 0 ? void 0 : onItemInit(keyRef.current, {
            level: levelRef.current,
            size: sizeRef.current,
            sizeWidthMap: resMap,
        }, {
            setTapSize,
            getKey,
        });
        return () => {
            onItemUnmount === null || onItemUnmount === void 0 ? void 0 : onItemUnmount(keyRef.current);
        };
    }, []);
    useEffect(() => {
        const itemWrap = itemWrapRef === null || itemWrapRef === void 0 ? void 0 : itemWrapRef.current;
        if (!itemWrap)
            return;
        const resizeObserver = new ResizeObserver(entries => {
            const { width } = entries[0].contentRect;
            onOperTabItemResizeRef === null || onOperTabItemResizeRef === void 0 ? void 0 : onOperTabItemResizeRef(keyRef.current, {
                width: width + gapSize,
                level: levelRef.current,
                size: sizeRef.current,
            });
        });
        resizeObserver.observe(itemWrap);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    const childrenContent = useMemo(() => {
        if (typeof props.children === 'function') {
            try {
                const fn = props.children;
                return fn(size);
            }
            catch (e) {
                return React.createElement(React.Fragment, null);
            }
        }
        return props.children;
    }, [props.children, size]);
    const domProps = useMemo(() => {
        return Object.assign(Object.assign({}, props), { tabkey: undefined, level: undefined, gapSize: undefined, children: undefined, defaultWidth: undefined, smallWidth: undefined, miniWidth: undefined });
    }, [props]);
    return (React.createElement("div", Object.assign({}, domProps, { ref: itemWrapRef }), childrenContent));
};
export { AutoSizeOperTabItem };
export default AutoSizeOperTab;
